import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { BrutalCard, BrutalButton } from "../components/Brutal";
import { byProductName } from "../lib/utils";
import { ListChecks, MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api`, withCredentials: true });

export default function StockCountPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({}); // product_id -> entered value (string)
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () =>
    api.get("/products").then((r) =>
      setProducts([...r.data].sort((a, b) =>
        (a.category || "").localeCompare(b.category || "") || byProductName(a, b)))
    );
  useEffect(() => { load(); }, []);

  const rows = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [products, search]
  );
  const entered = Object.entries(counts).filter(([, v]) => v !== "");
  const variances = entered.filter(([id, v]) => {
    const p = products.find((x) => x.id === id);
    return p && Number(v) !== Number(p.current_stock || 0);
  });

  const submit = async () => {
    if (!entered.length) return;
    if (!window.confirm(t("stock_count.confirm", { n: entered.length, v: variances.length }))) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/stock-count", {
        items: entered.map(([id, v]) => ({ product_id: id, counted: Math.max(0, Number(v) || 0) })),
      });
      setResult(r.data);
      setCounts({});
      toast.success(t("stock_count.done", {
        adjusted: r.data.adjusted.length, matched: r.data.matched,
      }));
      await load();
    } catch {
      toast.error(t("stock_count.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5" data-testid="stock-count-page">
      <div className="flex items-center gap-3">
        <ListChecks size={22} weight="bold" className="text-[#8a6a1c]" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">{t("stock_count.title")}</h1>
          <p className="text-sm text-zinc-500">{t("stock_count.subtitle")}</p>
        </div>
      </div>

      {result && result.adjusted.length > 0 && (
        <BrutalCard className="p-4 border-[#c9a24a]" data-testid="stock-count-result">
          <p className="text-sm font-semibold text-zinc-900 mb-2">
            {t("stock_count.result_title", { n: result.adjusted.length, matched: result.matched })}
          </p>
          <div className="space-y-1">
            {result.adjusted.map((a) => (
              <div key={a.product_id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-700">{a.name}</span>
                <span className="font-mono">
                  {a.before} → {a.counted}{" "}
                  <span className={a.delta > 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
                    ({a.delta > 0 ? "+" : ""}{a.delta})
                  </span>
                </span>
              </div>
            ))}
          </div>
        </BrutalCard>
      )}
      {result && result.adjusted.length === 0 && (
        <BrutalCard className="p-4 flex items-center gap-2 text-emerald-700" data-testid="stock-count-all-match">
          <CheckCircle size={18} weight="bold" />
          <span className="text-sm font-medium">{t("stock_count.all_match", { matched: result.matched })}</span>
        </BrutalCard>
      )}

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("stock_count.search")}
          className="w-full border border-zinc-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a24a]"
          data-testid="stock-count-search"
        />
      </div>

      <BrutalCard className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left font-mono uppercase tracking-wider text-zinc-400 text-xs">
              <th className="px-4 py-2.5 font-medium">{t("stock_count.product")}</th>
              <th className="px-3 py-2.5 font-medium text-right">{t("stock_count.system")}</th>
              <th className="px-4 py-2.5 font-medium text-right w-32">{t("stock_count.counted")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const v = counts[p.id] ?? "";
              const diff = v !== "" ? Number(v) - Number(p.current_stock || 0) : null;
              return (
                <tr key={p.id} className="border-b border-zinc-50 last:border-0">
                  <td className="px-4 py-2 font-medium text-zinc-900">{p.name}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">{p.current_stock}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      {diff !== null && diff !== 0 && (
                        <span className={`text-xs font-mono font-semibold ${diff > 0 ? "text-emerald-700" : "text-red-700"}`}>
                          {diff > 0 ? "+" : ""}{diff}
                        </span>
                      )}
                      <input
                        type="number"
                        min="0"
                        value={v}
                        onChange={(e) => setCounts((c) => ({ ...c, [p.id]: e.target.value }))}
                        placeholder="—"
                        className={`w-20 border rounded-md px-2 py-1 text-right font-mono focus:outline-none focus:ring-2 focus:ring-[#c9a24a] ${
                          diff !== null && diff !== 0 ? "border-[#c9a24a] bg-[#fbf6e7]" : "border-zinc-300"
                        }`}
                        data-testid={`stock-count-input-${p.id}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </BrutalCard>

      <div className="sticky bottom-4 flex justify-end">
        <BrutalButton
          onClick={submit}
          disabled={busy || entered.length === 0}
          data-testid="stock-count-submit"
        >
          {busy ? t("stock_count.saving") : t("stock_count.submit", { n: entered.length, v: variances.length })}
        </BrutalButton>
      </div>
    </div>
  );
}
